import { Action } from 'redux';
import { createSelector } from 'reselect';

import { getLoggedInUser } from '@paragon/login-tools';
import { assertExhaustiveSwitch, HotkeyActionResolver, Keybindings } from '@paragon/utilities';

import { getComponentSearchInput } from '../../components/interface/panels/navigation-panel/ComponentSearchField';
import {
  BEARING_ENVELOPE_SELECTABLE_TYPES,
  SelectableEntryOfType,
  SelectableItemEntry,
  SelectableItemType,
} from '../../library/selection/types';
import { isCurrentSelectionTransformable } from '../../library/transformations/selectors';
import { focusCameraOnSelectedObjects } from '../../three/ThreeJsSceneActions';
import { setRenderModeAndEnsureSelectedItemIsAppropriate, toggleLayout3DCamera } from '../actions/cameraActions';
import { deleteAllSelectedEntries } from '../actions/deletionActions';
import { redo, undo } from '../actions/editorActions';
import {
  runEscapeHotkey,
  runInsertHotkey,
  runParagonProcessHotkey,
  runToggleLockToBaseHotkey,
} from '../actions/globalHotkeyActions';
import { decrementBasicInsertionPlurality, incrementBasicInsertionPlurality } from '../actions/insertActions';
import { beginTransformation } from '../actions/layout/transformationActions';
import {
  designNewComponent,
  designOrRegenerateComponents,
  linkOrUnlinkComponents,
  startTrimOrExtendEnvelopes,
} from '../actions/layout/trussEnvelopeActions';
import { toggleLeftSidebar, toggleRightSidebarIfPossible } from '../actions/navigationPanelActions';
import { showChooseProjectDialog, showDownloadsDialog, toggleHotkeyDialog } from '../actions/popupActions';
import { moveToNextComponent } from '../actions/selectActions';
import { ThunkActionReturn } from '../actions/thunkActions';
import { cycleTrussEnvelopeRenderMode, toggleShowReferenceLines } from '../actions/visualOptionActions';
import { toggleDimensionMode } from '../slices/applicationSlice';
import { leftSidebarSet } from '../slices/panelDataSlice';
import { getDownloadingEnabledSelector } from './downloadSelectors';
import { getSelectedItemEntries } from './getSelectedItemSelector';
import { isInteractiveEditActive } from './interactiveEditingSelectors';
import { isInsertingComponentDesign } from './isInsertingComponentDesignSelector';
import { isLayoutViewActive } from './layoutModeSelectors';
import { getPickingObjectInfo } from './pickingSelectors';
import { inViewOnlyMode as componentIsViewOnlySelector } from './viewOnlyModeSelectors';

export type HotKeyActionType =
  | 'cuttingMode'
  | 'cycleTrussEnvelopeRenderMode'
  | 'decrementBasicInsertionPlurality'
  | 'delete'
  | 'designEnvelopes'
  | 'designNewEnvelope'
  | 'designView'
  | 'download'
  | 'duplicate'
  | 'escape'
  | 'find'
  | 'focusCamera'
  | 'hotkeys'
  | 'incrementBasicInsertionPlurality'
  | 'insert'
  | 'layoutView'
  | 'move'
  | 'moveToNextComponent'
  | 'redo'
  | 'runParagonProcess'
  | 'selectEnvelopeDesign'
  | 'showChooseProjectDialog'
  | 'toggle3dView'
  | 'toggleDimensionMode'
  | 'toggleLeftSidebar'
  | 'toggleLockToBase'
  | 'toggleReferenceLines'
  | 'toggleRightSidebar'
  | 'undo';

export const DEFAULT_KEYBINDINGS: Keybindings<HotKeyActionType> = Object.freeze({
  cuttingMode: [
    { keyCode: 'ShiftLeft', modifierKeys: { shiftKey: true } },
    { keyCode: 'ShiftRight', modifierKeys: { shiftKey: true } },
  ],
  cycleTrussEnvelopeRenderMode: [{ keyCode: 'KeyE', modifierKeys: { ctrlKey: true } }],
  delete: [{ keyCode: 'Delete', modifierKeys: {} }],
  decrementBasicInsertionPlurality: [
    { keyCode: 'NumpadSubtract', modifierKeys: {} },
    { keyCode: 'Minus', modifierKeys: {} },
    { keyCode: 'Minus', modifierKeys: { shiftKey: true } },
  ],
  designEnvelopes: [{ keyCode: 'KeyG', modifierKeys: { ctrlKey: true } }],
  designNewEnvelope: [{ keyCode: 'KeyG', modifierKeys: { ctrlKey: true, altKey: true } }],
  designView: [{ keyCode: 'Digit2', modifierKeys: { ctrlKey: true } }],
  download: [{ keyCode: 'KeyD', modifierKeys: { ctrlKey: true, shiftKey: true } }],
  duplicate: [{ keyCode: 'KeyC', modifierKeys: { ctrlKey: true } }],
  escape: [{ keyCode: 'Escape', modifierKeys: {} }],
  find: [{ keyCode: 'KeyF', modifierKeys: { ctrlKey: true } }],
  focusCamera: [{ keyCode: 'Period', modifierKeys: { ctrlKey: true } }],
  hotkeys: [{ keyCode: 'Slash', modifierKeys: { ctrlKey: true } }],
  incrementBasicInsertionPlurality: [
    { keyCode: 'NumpadAdd', modifierKeys: {} },
    { keyCode: 'Equal', modifierKeys: {} },
    { keyCode: 'Equal', modifierKeys: { shiftKey: true } },
  ],
  insert: [{ keyCode: 'KeyQ', modifierKeys: { ctrlKey: true }, allowMacCtrlKey: true }],
  layoutView: [{ keyCode: 'Digit1', modifierKeys: { ctrlKey: true } }],
  move: [{ keyCode: 'KeyM', modifierKeys: { ctrlKey: true } }],
  moveToNextComponent: [{ keyCode: 'Space', modifierKeys: {} }],
  redo: [{ keyCode: 'KeyY', modifierKeys: { ctrlKey: true } }],
  runParagonProcess: [{ keyCode: 'KeyA', modifierKeys: { ctrlKey: true } }],
  selectEnvelopeDesign: [{ keyCode: 'KeyG', modifierKeys: { ctrlKey: true, shiftKey: true } }],
  showChooseProjectDialog: [{ keyCode: 'KeyP', modifierKeys: { ctrlKey: true, shiftKey: true } }],
  toggle3dView: [{ keyCode: 'Digit3', modifierKeys: { ctrlKey: true } }],
  toggleDimensionMode: [{ keyCode: 'KeyD', modifierKeys: { ctrlKey: true } }],
  toggleLeftSidebar: [{ keyCode: 'Comma', modifierKeys: {} }],
  toggleLockToBase: [{ keyCode: 'KeyB', modifierKeys: { ctrlKey: true } }],
  toggleReferenceLines: [{ keyCode: 'Digit9', modifierKeys: { ctrlKey: true } }],
  toggleRightSidebar: [{ keyCode: 'Period', modifierKeys: {} }],
  undo: [{ keyCode: 'KeyZ', modifierKeys: { ctrlKey: true } }],
} satisfies Keybindings<HotKeyActionType>);

export const getHotkeyActionResolver = createSelector(
  componentIsViewOnlySelector,
  isInsertingComponentDesign,
  getSelectedItemEntries,
  getPickingObjectInfo,
  isLayoutViewActive,
  getLoggedInUser,
  getDownloadingEnabledSelector,
  isCurrentSelectionTransformable,
  isInteractiveEditActive,
  (
    componentIsViewOnly,
    insertingComponentDesign,
    selectedItems,
    pickingObjectInfo,
    inLayoutMode,
    loggedInUser,
    downloadingEnabled,
    selectionTransformable,
    interactiveEditActive,
  ): HotkeyActionResolver<HotKeyActionType, Action | ThunkActionReturn<void | Promise<void>>> =>
    (type, keyup) => {
      if (keyup) return null;

      switch (type) {
        case 'cuttingMode':
          if (inLayoutMode) {
            if (componentIsViewOnly) return null;

            if (interactiveEditActive) return null;

            if (
              !onlyOneOrMoreItemsSelectedOfType(selectedItems, ['trussEnvelope', ...BEARING_ENVELOPE_SELECTABLE_TYPES])
            ) {
              return null;
            }

            // Can't start trim/extend when we're already trying to pick something else.
            return pickingObjectInfo == null
              ? { type, resolution: startTrimOrExtendEnvelopes({ repeat: true }) }
              : null;
          } else {
            // FIXME Member cutting mode has not been incorporated into the hotkey action resolver
            return null;
          }
        case 'cycleTrussEnvelopeRenderMode':
          return { type, resolution: cycleTrussEnvelopeRenderMode() };

        case 'delete':
          return insertingComponentDesign || !componentIsViewOnly
            ? { type, resolution: deleteAllSelectedEntries() }
            : null;

        case 'decrementBasicInsertionPlurality':
          return { type, resolution: decrementBasicInsertionPlurality() };

        case 'designEnvelopes': {
          if (componentIsViewOnly) return null;

          const selectedEnvelopeEntries = onlyOneOrMoreItemsSelectedOfType(selectedItems, ['trussEnvelope']);
          if (!selectedEnvelopeEntries) return null;
          const selectedEnvelopes = selectedEnvelopeEntries.map(({ item }) => item);

          return { type, resolution: designOrRegenerateComponents(selectedEnvelopes) };
        }

        case 'designNewEnvelope': {
          if (componentIsViewOnly) return null;

          const selectedEnvelopeEntries = onlyOneOrMoreItemsSelectedOfType(selectedItems, ['trussEnvelope']);
          if (!selectedEnvelopeEntries) return null;
          const selectedEnvelopes = selectedEnvelopeEntries.map(({ item }) => item);

          return { type, resolution: designNewComponent(selectedEnvelopes) };
        }

        case 'designView':
          return { type, resolution: setRenderModeAndEnsureSelectedItemIsAppropriate('design') };

        case 'download':
          return downloadingEnabled ? { type, resolution: showDownloadsDialog() } : null;

        case 'duplicate':
          return selectionTransformable && !window.getSelection()?.toString()
            ? { type, resolution: beginTransformation('movement', { duplicate: true }) }
            : null;

        case 'escape':
          return { type, resolution: runEscapeHotkey() };

        case 'find':
          return {
            type,
            resolution: (dispatch) => {
              const searchInput = getComponentSearchInput();

              if (searchInput) {
                searchInput.focus();
              } else {
                // This can't occur within a key event handler or the DOM animation will be very janky.
                setTimeout(() => {
                  dispatch(leftSidebarSet('open'));
                  setTimeout(() => getComponentSearchInput()?.focus(), 500);
                });
              }
            },
          };

        case 'focusCamera':
          return { type, resolution: () => void focusCameraOnSelectedObjects() };

        case 'hotkeys':
          return { type, resolution: toggleHotkeyDialog() };

        case 'incrementBasicInsertionPlurality':
          return { type, resolution: incrementBasicInsertionPlurality() };

        case 'insert':
          return { type, resolution: runInsertHotkey() };

        case 'layoutView':
          return { type, resolution: setRenderModeAndEnsureSelectedItemIsAppropriate('layout') };

        case 'move':
          return selectionTransformable ? { type, resolution: beginTransformation('movement') } : null;

        case 'moveToNextComponent':
          return {
            type,
            resolution: (dispatch) => {
              const { activeElement } = document;
              if (activeElement && activeElement instanceof HTMLElement) activeElement.blur();

              dispatch(moveToNextComponent());
            },
          };

        case 'redo':
          return !componentIsViewOnly ? { type, resolution: redo() } : null;

        case 'runParagonProcess':
          return !componentIsViewOnly ? { type, resolution: runParagonProcessHotkey() } : null;

        case 'selectEnvelopeDesign': {
          if (componentIsViewOnly) return null;

          const selectedEnvelopeEntries = onlyOneOrMoreItemsSelectedOfType(selectedItems, ['trussEnvelope']);
          if (!selectedEnvelopeEntries) return null;
          const selectedEnvelopes = selectedEnvelopeEntries.map(({ item }) => item);

          return { type, resolution: linkOrUnlinkComponents(selectedEnvelopes) };
        }

        case 'showChooseProjectDialog':
          return loggedInUser ? { type, resolution: showChooseProjectDialog() } : null;

        case 'toggle3dView':
          return { type, resolution: toggleLayout3DCamera() };

        case 'toggleDimensionMode':
          return { type, resolution: toggleDimensionMode() };

        case 'toggleLeftSidebar':
          return { type, resolution: toggleLeftSidebar() };

        case 'toggleLockToBase':
          return { type, resolution: runToggleLockToBaseHotkey() };

        case 'toggleReferenceLines':
          return { type, resolution: toggleShowReferenceLines() };

        case 'toggleRightSidebar':
          return { type, resolution: toggleRightSidebarIfPossible() };

        case 'undo':
          return !componentIsViewOnly ? { type, resolution: undo() } : null;

        default:
          assertExhaustiveSwitch(type);
      }
    },
);

/**
 * Determines whether all items selected are of the allowed types and returns them if so. Empty `items` or items not
 * matching returns `null`.
 */
const onlyOneOrMoreItemsSelectedOfType = <T extends SelectableItemType>(
  items: readonly SelectableItemEntry[],
  types: readonly T[],
): readonly SelectableEntryOfType<T>[] | null =>
  items.length > 0 && items.every((item) => (types as unknown as SelectableItemType[]).includes(item.type))
    ? (items as readonly SelectableEntryOfType<T>[])
    : null;
